/**
 * Pure view model of the dashboard (task T.C2): filtering, sorting, the
 * drill-down link and the derived column values.
 *
 * The api owns filtering by region (contract #11 takes `?region=`) — this
 * module never filters venues, it only derives the option list and the sort
 * order, which are presentation concerns the contract does not carry.
 */

import type { DashboardRowDto, DashboardVenueKind, PartnerPoolDto, StatusCountMap } from '../../api/dashboard';
import { sumByStatus } from './status';
import { parseDepartmentBucket, type DepartmentBucket } from '../../departments';
// T.Y1 — the «Тип площадки» fold is the ROSTER's (A5/T.Z18), reused verbatim: the same
// two tokens, the same coercion of a hand-edited value. The pure-model layer is the one
// place the two pages can share it (venues-model already reads this page's format/status).
import { parseKindParam, venueKindCounts } from '../venues/venues-model';

// ---------------------------------------------------------------------------
// URL-synced state
// ---------------------------------------------------------------------------

/** Query params the dashboard owns (see lib/query-params). */
export const PARAM_REGION = 'region';
export const PARAM_SORT = 'sort';
export const PARAM_DIR = 'dir';
/**
 * ADDITIVE (T.Z42, v1.8.7; SERVER-SIDE since T.Z56, v1.8.9) — the «Отдел» filter.
 * T.Z42 narrowed the rows in the browser; the owner's 14.09 request made the CARDS
 * answer the filter too, and the campaign block is a server-side aggregate — so #11
 * takes `?department=` now and narrows the whole response (cards + rows) in one bound
 * parameter. The URL carries it like every other filter of this page.
 */
export const PARAM_DEPARTMENT = 'department';

/**
 * ADDITIVE (T.Y1, v1.9.35, owner 18.09) — the «Тип площадки» filter. The ROSTER page
 * already has this filter (A5/T.Z18, client-side over the kind every row carries); the
 * dashboard takes the same URL key, the same two values and the same default — absent =
 * «вместе».
 *
 * REWORKED (T.Z144, owner 18.09) — the kind is a QUOTA AXIS, not a row filter: a selected
 * kind names which plan the table reads (offline / online), which venues stay (only those
 * with a quota ON THAT AXIS — see {@link venueTables}) and what the register drill-ins
 * carry ({@link kindFormat}). The T.Z68 «Онлайн-филиалы» section is gone; the online axis
 * lives in its own «Онлайн-площадки» table instead.
 *
 * CORRECTED (T.Z145, owner 19.09) — T.Z144 shipped the axis over a KIND-NARROWED read
 * (`?kind=` on #11, v1.9.35) plus a client-side kind guard in the fold, and the two
 * together hid every REGISTRY venue that carries an ONLINE quota from the «Онлайн» view
 * (Орский нефтяной техникум, Школа № 878, ВолгГТУ-Камышинский — the owner's own
 * examples): the api returned only `PARTNER_ONLINE` rows under `?kind=PARTNER_ONLINE`,
 * and the guard dropped the rest. The owner: «we have to use quota based approach — show
 * there those who have online quota». The read is UN-narrowed now (the page sends
 * `region × department × format`, never `kind` — #11 keeps ACCEPTING `?kind=`, the
 * contract v1.9.35 parameter, it is just no longer this page's caller), and the fold
 * quota-cuts every row that arrives. See {@link venueTables}.
 */
export const PARAM_KIND = 'kind';

/** Coerces the URL param: the roster's own parser — a hand-edited `?kind=онлайн` is «вместе», not a 400. */
export function parseKindFilterParam(value: string | undefined): DashboardVenueKind | undefined {
  return parseKindParam(value);
}

/**
 * The «Тип площадки» options in the owner's words (18.09: «онлайн / оффлайн / вместе»),
 * counts included when the #6 directory answered — the same source the roster's options
 * count from. The labels differ from the roster's («вместе» vs «Все», «Онлайн» vs
 * «Онлайн-филиалы») because the owner named this filter's three states verbatim; the
 * VALUES and the fold are the roster's, so one kind cannot mean two things.
 */
export function kindFilterOptions(
  directory: ReadonlyArray<{ kind?: DashboardVenueKind }> | null | undefined,
): Array<{ value: string; label: string; title: string }> {
  const counts = venueKindCounts(directory);
  const all = counts ? `Вместе (${counts.registry + counts.partnerOnline})` : 'Вместе';
  const registry = counts ? `Офлайн (${counts.registry})` : 'Офлайн';
  const partner = counts ? `Онлайн (${counts.partnerOnline})` : 'Онлайн';
  // T.Z145 — the tooltips describe the QUOTA fold the tables now cut (the counts stay
  // the roster's kind counts: they count venues, the tables read the plan of the axis).
  return [
    { value: '', label: all, title: 'Реестровые площадки и филиалы партнёров' },
    {
      value: 'REGISTRY',
      label: registry,
      title: 'Площадки с офлайн-квотой (план офлайн > 0) — любого «Типа»',
    },
    {
      value: 'PARTNER_ONLINE',
      label: partner,
      title: 'Площадки с онлайн-квотой (план онлайн > 0) — филиалы партнёра и реестровые с собственным планом',
    },
  ];
}

/** The toolbar-meta caption of an active kind filter («· Офлайн»), absent = nothing. */
export function kindFilterLabel(kind: DashboardVenueKind | undefined): string {
  if (kind === 'REGISTRY') return 'Офлайн';
  if (kind === 'PARTNER_ONLINE') return 'Онлайн';
  return '';
}

/**
 * ADDITIVE (T.Z144, v1.9.37) — the «Формат обучения» value the register speaks for the
 * selected kind: «Офлайн» drills into `format=OFFLINE`, «Онлайн» into `format=ONLINE`,
 * «вместе» sends nothing (a default state never leaks a parameter — the page's own rule).
 *
 * This is the ONE mapping behind three facts of the page, and they hold together or not
 * at all: the #11 read narrows its application buckets by the same `format=` (so a card
 * prints the count the register answers), every register drill-in carries it, and the
 * table's fill reads the quota axis of the same word. #11 has taken the parameter since
 * v1.2 (T.F4); v1.9.37 documents it again and the row carries the matching online fill
 * (`fillPercentOnline`).
 */
export function kindFormat(kind: DashboardVenueKind | undefined): 'OFFLINE' | 'ONLINE' | undefined {
  if (kind === 'REGISTRY') return 'OFFLINE';
  if (kind === 'PARTNER_ONLINE') return 'ONLINE';
  return undefined;
}

/** Coerces the URL param; a hand-edited value falls back to «Все отделы». */
export function parseDepartmentParam(value: string | undefined): DepartmentBucket | undefined {
  return parseDepartmentBucket(value);
}

export type SortKey = 'fillPercent' | 'plan' | 'name';
export type SortDir = 'asc' | 'desc';

/** Sortable columns, in menu order. */
const SORT_KEYS: ReadonlyArray<SortKey> = ['fillPercent', 'plan', 'name'];

/** A column starts in the direction a manager means when they click it. */
export function defaultDirFor(sort: SortKey): SortDir {
  return sort === 'name' ? 'asc' : 'desc';
}

/** Coerces the URL param; anything else falls back to the default column. */
export function parseSort(value: string | undefined): SortKey {
  return SORT_KEYS.includes(value as SortKey) ? (value as SortKey) : 'fillPercent';
}

/** Coerces the URL param; anything else falls back to the column's default. */
export function parseDir(value: string | undefined, sort: SortKey): SortDir {
  return value === 'asc' || value === 'desc' ? value : defaultDirFor(sort);
}

// ---------------------------------------------------------------------------
// The plan of a venue's OWN type (D1/A14, wave 9)
// ---------------------------------------------------------------------------

/** «РЕЕСТР» / «ПАРТНЕР-ОНЛАЙН» — the badge a dashboard row and a venue card carry. */
export function kindBadgeLabel(kind: DashboardVenueKind | undefined): string {
  return kind === 'PARTNER_ONLINE' ? 'ПАРТНЕР-ОНЛАЙН' : 'РЕЕСТР';
}

/** True for a partner filial — the row whose plan is the partner's SHARED pool. */
export function isPartnerRow(row: Pick<DashboardRowDto, 'kind'>): boolean {
  return row.kind === 'PARTNER_ONLINE';
}

/**
 * The plan of the venue's OWN type — `planOffline` for a registry venue, the
 * partner's shared pool for a branch. The legacy `plan` is the fallback for a
 * payload an older api sent, and the two types are NEVER added together.
 *
 * T.Z57: the «План» COLUMN is the offline section's only now — the partner
 * section hides it (the pool is shared, a branch's own plan is 0, the column
 * read the same number fifteen times). The api still sends the pool and this
 * helper still reads it: the value is what ORDERS the section (`sortRows`
 * below), it just has no cell to show in.
 */
export function ownPlanOf(row: DashboardRowDto): number {
  if (isPartnerRow(row)) return row.planOnline ?? row.plan;
  return row.planOffline ?? row.plan;
}

/**
 * The fill of the venue's OWN type: the offline fill where the api sends it,
 * the legacy one otherwise. Same T.Z57 note as {@link ownPlanOf} — the value
 * still sorts the row wherever it shows.
 */
export function ownFillOf(row: DashboardRowDto): number {
  if (isPartnerRow(row)) return row.fillPercent;
  return row.fillPercentOffline ?? row.fillPercent;
}

/**
 * ADDITIVE (T.Z144, v1.9.37) — the plan of the ONLINE axis: the venue's own
 * `venue_plans.plan_online` on a registry row, the partner's SHARED pool on a
 * branch — the api composes both into `planOnline`, exactly the number the
 * «Онлайн» table's «План» cell prints and `fillPercentOnline`'s denominator.
 * `0` when an older api sent no per-axis block (the row then has no online
 * quota to show, and {@link venueTables} keeps it out of the online table).
 */
export function onlinePlanOf(row: DashboardRowDto): number {
  return row.planOnline ?? 0;
}

/**
 * ADDITIVE (T.Z144, v1.9.37) — the ONLINE fill of the row: the api's
 * `fillPercentOnline` (`onlineApplications / planOnline`), `0` when an older
 * api sent no per-axis block. The online table's bar and its plan sort both
 * read this pair, never the offline axis.
 */
export function onlineFillOf(row: DashboardRowDto): number {
  return row.fillPercentOnline ?? 0;
}

/**
 * ADDITIVE (T.Z145, owner 19.09) — the plan of the OFFLINE axis, read the same way
 * {@link onlinePlanOf} reads the online one: `venue_plans.plan_offline` of EVERY row,
 * a partner filial included (under quota rules a branch may carry an offline plan of
 * its own, and the fold must not hide it — there is no kind guard on any table). The
 * own-type pair ({@link ownPlanOf}) is the FALLBACK, so a payload an older api sent
 * still renders the offline table off the legacy `plan` instead of emptying it.
 */
export function offlinePlanOf(row: DashboardRowDto): number {
  return row.planOffline ?? ownPlanOf(row);
}

/**
 * ADDITIVE (T.Z145) — the OFFLINE fill twin of {@link offlinePlanOf}: the api's
 * `fillPercentOffline` on every row, the own-type fill as the older-payload fallback.
 * The offline tables' bars and plan sorts read this pair, never the online one.
 */
export function offlineFillOf(row: DashboardRowDto): number {
  return row.fillPercentOffline ?? ownFillOf(row);
}

/**
 * ADDITIVE (T.Z156, v1.9.45, owner 21.09) — the `byStatus` map a table's «Заявления»
 * cell renders: the map of the TABLE'S OWN quota axis, not the whole-funnel one.
 *
 * The whole-funnel `byStatus` mixes both formats, and the owner caught the result on
 * prod: the МФЮА row printed «1 | 18» (offline children) beside a 0% online fill —
 * «you show offline students in the online table». The api now slices the same six
 * statuses per axis (`onlineByStatus` / `offlineByStatus`, v1.9.45), and every
 * quota-based table reads its own: «Онлайн» tables (the regular one AND the partner
 * group) read the online map, «Офлайн» tables the offline one.
 *
 * A payload an older api sent carries neither map — the cell degrades to the
 * whole-funnel `byStatus` instead of emptying out (the exact fallback idiom the plan
 * and fill readers of this module follow). The whole-funnel map keeps every
 * campaign-level reader: the summary cards never go through this helper.
 */
export function axisByStatusOf(row: DashboardRowDto, axis?: VenueAxis): StatusCountMap {
  if (axis === 'ONLINE') return row.onlineByStatus ?? row.byStatus;
  if (axis === 'OFFLINE') return row.offlineByStatus ?? row.byStatus;
  return row.byStatus;
}

// ---------------------------------------------------------------------------
// The column plan of a section (T.Z57 → T.Z144)
// ---------------------------------------------------------------------------

/** One column of a table — its key, its caption and how it behaves. */
export interface SectionColumn {
  /** Stable identity — the React key and the unit test's handle. */
  readonly key: 'name' | 'kind' | 'region' | 'plan' | 'applications' | 'fillPercent' | 'control';
  readonly label: string;
  /** Set when the header sorts — the button carries this key into the URL. */
  readonly sortKey?: SortKey;
  /** Right-aligned number column. */
  readonly numeric?: boolean;
}

/** The columns of a table, in reading order (seven since wave 9's «Тип»). */
const SECTION_COLUMNS: ReadonlyArray<SectionColumn> = [
  { key: 'name', label: 'Площадка', sortKey: 'name' },
  { key: 'kind', label: 'Тип' },
  { key: 'region', label: 'Регион' },
  { key: 'plan', label: 'План', sortKey: 'plan', numeric: true },
  { key: 'applications', label: 'Заявления' },
  { key: 'fillPercent', label: 'Заполненность', sortKey: 'fillPercent' },
  { key: 'control', label: 'Контроль' },
];

/**
 * The columns every table renders. T.Z57 (owner, 14.09) had dropped «План» and
 * «Заполненность» from a separate partner section; T.Z144 (owner, 18.09) removed that
 * section and gave the online axis a real table of its own — the SAME formatting as the
 * offline one, with the cells reading the online plan and the online fill. One column
 * list, no variant.
 */
export function sectionColumns(): ReadonlyArray<SectionColumn> {
  return SECTION_COLUMNS;
}

// ---------------------------------------------------------------------------
// The two tables of the page (T.Z144, owner 18.09: «Тип площадки» = a quota axis;
// T.Z145, owner 19.09: the fold is QUOTA-based over an un-narrowed read)
// ---------------------------------------------------------------------------

/** Which quota axis a table's «План»/«Заполненность» cells read and its fold cuts by. */
export type VenueAxis = 'OFFLINE' | 'ONLINE';

/** One venue table the page renders: its title, its rows, its axis, its pager. */
export interface VenueTablePlan {
  /** The heading. T.Z144: the title follows the filter — «Офлайн» / «Онлайн». */
  title: string;
  /** The FULL quota-cut of the axis, already sorted — see {@link venueTables}. */
  rows: DashboardRowDto[];
  /**
   * The quota axis the «План»/«Заполненность» cells read — offline
   * ({@link offlinePlanOf} / {@link offlineFillOf}) or online
   * ({@link onlinePlanOf} / {@link onlineFillOf}). Every table names one (T.Z145).
   */
  axis?: VenueAxis;
}

/**
 * ADDITIVE (T.Z149, owner 20.09, v1.9.41) — one partner's group table: its branch rows
 * PLUS the ONE shared-quota fact the group is about. A partner's filials hold NO quotas
 * of their own — `partners.plan_online` is the single denominator for all of them — so
 * the table replaces the per-venue «Заполненность» with ONE progress bar rendered from
 * `plan`/`filled`, while the rows keep the online table's other columns (and their
 * pagination and sorting idioms).
 */
export interface PartnerTablePlan {
  /** `partners.id` — the React key and the `data-partner-id` marker. */
  partnerId: number;
  /** `partners.name` — the caption's first word. */
  partnerName: string;
  /** The heading: «Академия Топ (общая квота)» — see {@link partnerTableTitle}. */
  title: string;
  /** The partner's branch rows, sorted on the ONLINE axis like every online table. */
  rows: DashboardRowDto[];
  /** The SHARED pool (`partners.plan_online`), read ONCE per partner — never Σ rows. */
  plan: number;
  /** Σ `onlineApplications` over the partner's rows — the ONE bar's numerator. */
  filled: number;
  /** `filled / plan × 100`, `0` when the pool is 0. */
  fillPercent: number;
}

/** The heading a partner's group table carries — Russian, the owner's own example shape. */
export function partnerTableTitle(partnerName: string): string {
  return `${partnerName} (общая квота)`;
}

/** `filled / plan × 100` at a tenth — the api's own `fillPercentOf`, mirrored for the model's fallback fold. */
function poolFillPercentOf(filled: number, plan: number): number {
  if (!Number.isFinite(plan) || plan <= 0) return 0;
  return Math.round((filled / plan) * 1000) / 10;
}

/**
 * The venue tables the page renders for the active «Тип площадки» filter, in one pure
 * function so the unit suite can pin the fold without a DOM.
 *
 * THE FOLD IS QUOTA-BASED (T.Z145, owner 19.09: «we have to use quota based approach —
 * show there those who have online quota»). T.Z144 shipped it over a KIND-NARROWED read
 * plus a client-side kind guard, and the pair hid the REGISTRY venues that carry an
 * ONLINE quota from the «Онлайн» view (Орский нефтяной техникум, Школа № 878
 * (Москва), ВолгГТУ-Камышинский — the owner's own examples). Now every state cuts the
 * SAME way: membership is `plan > 0` ON THE TABLE'S AXIS, over EVERY row the read
 * returns, of either kind —
 *
 *   * «вместе» (absent kind) → the «Офлайн-площадки» table (`planOffline > 0`), the
 *     «Онлайн-площадки» table (`planOnline > 0` of a REGISTRY venue) and the partner
 *     GROUP tables below them (T.Z149);
 *   * `REGISTRY` («Офлайн») → ONE table titled «Офлайн»: `planOffline > 0`, the offline
 *     plan/fill in its cells — no partner group (the axis is offline);
 *   * `PARTNER_ONLINE` («Онлайн») → ONE table titled «Онлайн» (the registry venues with
 *     an own online plan) plus the partner group tables.
 *
 * THE PARTNER BRANCHES LEAVE THE REGULAR ONLINE TABLE (T.Z149, owner 20.09): a partner's
 * filials hold NO quotas of their own — `partners.plan_online` is the ONE denominator for
 * all of them — so a per-branch «Заполненность» misrepresents the group. The rows move
 * into their own group table(s) ({@link PartnerTablePlan}), one per partner, titled
 * «<partner> (общая квота)», whose «Заполненность» is ONE progress bar over the SHARED
 * pool; the bar's numbers come from #11's `partnerPools` block (v1.9.41), and an older
 * payload without it degrades to the same fold computed from the rows themselves (the
 * caption then reads «Онлайн-филиалы партнёра», the only word the block alone carried).
 * The offline axis keeps NO kind guard (T.Z145 stands): a branch with an offline plan of
 * its own still lists there.
 *
 * Each table is sorted ON ITS OWN AXIS over its FULL cut (`sortRows` — the pager
 * slices AFTER the sort, never within it), so «План»/«Заполненность» order the cells
 * they sit above even where the axes disagree about a row (a branch: pool 2 600
 * online, 0 offline). `sort`/`dir` default to the page's own defaults, so a fold pin
 * needs no sort arguments.
 */
export function venueTables(
  rows: ReadonlyArray<DashboardRowDto>,
  kind: DashboardVenueKind | undefined,
  sort: SortKey = 'fillPercent',
  dir: SortDir = 'desc',
  pools?: ReadonlyArray<PartnerPoolDto>,
): { offline: VenueTablePlan | null; online: VenueTablePlan | null; partners: PartnerTablePlan[] } {
  // The quota cuts of BOTH axes, over every row that arrived. T.Z149 — the partner
  // branches leave the regular online cut (they sit in their own group tables below);
  // the offline axis keeps no kind guard (T.Z145): a branch with an offline plan of its
  // own still lists there.
  // T.Z198 (owner 25.09 — a region's venue with 13 children and plan 0/0 vanished from
  // both tables): a venue with NO plan but with APPLICATIONS on the axis stays visible —
  // an unfunded venue with a live funnel is exactly the row a manager has to notice
  // (its «Заполненность» reads 0%). Planless AND empty venues stay hidden (the
  // T.Z48 noise cut stands).
  const offlineRows = rows.filter(
    (row) => offlinePlanOf(row) > 0 || sumByStatus(axisByStatusOf(row, 'OFFLINE')) > 0,
  );
  const onlineRows = rows.filter(
    (row) =>
      !isPartnerRow(row) && (onlinePlanOf(row) > 0 || sumByStatus(axisByStatusOf(row, 'ONLINE')) > 0),
  );

  // T.Z149 — the partner GROUP tables: one per partner that holds a quota-carrying
  // branch in this response, the pool read ONCE per partner (never Σ over the rows).
  const partners = partnerTablesOf(rows, pools, sort, dir);

  if (kind === 'REGISTRY') {
    return {
      offline: {
        title: 'Офлайн',
        rows: sortRows(offlineRows, sort, dir, 'OFFLINE'),
        axis: 'OFFLINE',
      },
      online: null,
      // The «Офлайн» axis is offline: no partner group table, whatever the rows carry.
      partners: [],
    };
  }
  if (kind === 'PARTNER_ONLINE') {
    return {
      offline: null,
      online: {
        title: 'Онлайн',
        rows: sortRows(onlineRows, sort, dir, 'ONLINE'),
        axis: 'ONLINE',
      },
      partners,
    };
  }
  // «вместе» — both axes plus the partner groups over the whole roster.
  return {
    offline: {
      title: 'Офлайн-площадки',
      rows: sortRows(offlineRows, sort, dir, 'OFFLINE'),
      axis: 'OFFLINE',
    },
    online: {
      title: 'Онлайн-площадки',
      rows: sortRows(onlineRows, sort, dir, 'ONLINE'),
      axis: 'ONLINE',
    },
    partners,
  };
}

/**
 * ADDITIVE (T.Z149) — the partner group tables of a response: one per partner that holds
 * an online-quota branch among `rows`, in `partnerId` order.
 *
 * The NUMBERS come from #11's `partnerPools` block (v1.9.41) where it arrived — `plan`
 * is the partner's pool read once and `filled` the api's own Σ, so the bar can never
 * disagree with the server about the filter — and the ROWS are the rows the fold cut.
 * A partner whose block entry has no matching rows renders nothing (a quota-zero pool is
 * no group, the T.Z145 idiom); a partner whose rows arrived without a block entry (an
 * older api) derives `plan`/`filled` from the rows themselves and borrows the generic
 * «Онлайн-филиалы партнёра» caption, the only word the block alone carried.
 */
function partnerTablesOf(
  rows: ReadonlyArray<DashboardRowDto>,
  pools: ReadonlyArray<PartnerPoolDto> | undefined,
  sort: SortKey,
  dir: SortDir,
): PartnerTablePlan[] {
  // The partner rows, grouped per partner key (a payload an older api sent carries no
  // `partnerId`: every branch row is the ONE partner's — there is exactly one partner
  // row-space on this contract, and the key 0 stands in for «unnamed»).
  const grouped = new Map<number, DashboardRowDto[]>();
  for (const row of rows) {
    if (!isPartnerRow(row) || onlinePlanOf(row) <= 0) continue;
    const key = row.partnerId ?? 0;
    const bucket = grouped.get(key);
    if (bucket) bucket.push(row);
    else grouped.set(key, [row]);
  }

  const plans: PartnerTablePlan[] = [];
  const seen = new Set<number>();
  for (const pool of pools ?? []) {
    const bucket = grouped.get(pool.partnerId);
    if (!bucket || bucket.length === 0) continue;
    seen.add(pool.partnerId);
    plans.push({
      partnerId: pool.partnerId,
      partnerName: pool.partnerName,
      title: partnerTableTitle(pool.partnerName),
      rows: sortRows(bucket, sort, dir, 'ONLINE'),
      plan: pool.plan,
      filled: pool.filled,
      fillPercent: pool.fillPercent,
    });
  }
  // The groups the block does not know (an older api, or a drift between the two): the
  // same fold over the rows, the numbers derived where the block would have carried them.
  for (const [partnerId, bucket] of grouped) {
    if (seen.has(partnerId) || bucket.length === 0) continue;
    const plan = Math.max(...bucket.map(onlinePlanOf));
    const filled = bucket.reduce((sum, row) => sum + (row.onlineApplications ?? 0), 0);
    plans.push({
      partnerId,
      partnerName: FALLBACK_PARTNER_NAME,
      title: partnerTableTitle(FALLBACK_PARTNER_NAME),
      rows: sortRows(bucket, sort, dir, 'ONLINE'),
      plan,
      filled,
      fillPercent: poolFillPercentOf(filled, plan),
    });
  }
  return plans.sort((left, right) => left.partnerId - right.partnerId);
}

/** The caption's word when the api sent no `partnerPools` block (an older api). */
const FALLBACK_PARTNER_NAME = 'Онлайн-филиалы партнёра';

/**
 * The client-side page size of EVERY venue table (T.Z144 named it for the online one;
 * T.Z145 gave the offline tables the same pager): twenty rows — a page the pager's
 * «Показаны 1–20 из 41» caption can name honestly, and enough venues on one screen
 * that the fill column stays comparable by eye.
 */
export const VENUE_TABLE_PAGE_SIZE = 20;

/** Whole pages, minimum 1 — a non-empty table always has a page to show. */
export function totalPagesOf(total: number, pageSize: number): number {
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(pageSize) || pageSize <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

/** A pager state that survives the row set shrinking: never before the first page. */
export function clampPage(page: number, totalPages: number): number {
  const pages = Number.isFinite(totalPages) && totalPages >= 1 ? Math.floor(totalPages) : 1;
  const current = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
  return Math.min(current, pages);
}

/** The rows of one client-side page (1-based). */
export function pageSlice<T>(rows: ReadonlyArray<T>, page: number, pageSize: number): T[] {
  const safePage = clampPage(page, totalPagesOf(rows.length, pageSize));
  return rows.slice((safePage - 1) * pageSize, safePage * pageSize);
}

function compareRows(a: DashboardRowDto, b: DashboardRowDto, sort: SortKey, axis: VenueAxis | undefined): number {
  if (sort === 'name') return a.name.localeCompare(b.name, 'ru');
  // ADDITIVE (T.Z144, widened T.Z145) — the axis picks the numbers: on the ONLINE axis
  // the plan IS the online plan (a branch's pool of 2 600 must not drown a registry
  // venue's own online plan of 30), on the OFFLINE axis the row's own `planOffline` —
  // a branch must not borrow the pool into the offline order either. Without an axis
  // the own-type pair decides (the pre-T.Z144 reading, kept for the helper's callers).
  if (sort === 'plan') {
    if (axis === 'ONLINE') return onlinePlanOf(a) - onlinePlanOf(b);
    if (axis === 'OFFLINE') return offlinePlanOf(a) - offlinePlanOf(b);
    return ownPlanOf(a) - ownPlanOf(b);
  }
  if (axis === 'ONLINE') return onlineFillOf(a) - onlineFillOf(b);
  if (axis === 'OFFLINE') return offlineFillOf(a) - offlineFillOf(b);
  return ownFillOf(a) - ownFillOf(b);
}

/**
 * Sorted copy (never mutates the input — TanStack owns that array). Ties fall
 * back to `name`, then `venueId`, so two renders of the same data produce the
 * same order and the e2e can assert on row positions.
 *
 * ADDITIVE (T.Z144, widened T.Z145) — `axis` picks the numbers the plan/fill columns
 * compare: the online tables sort on the online axis, the offline ones on the offline
 * pair of EVERY row ({@link offlinePlanOf}, no kind guard), so the header a manager
 * clicked always orders the cells it sits above.
 */
export function sortRows(
  rows: ReadonlyArray<DashboardRowDto>,
  sort: SortKey,
  dir: SortDir,
  axis?: VenueAxis,
): DashboardRowDto[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const primary = compareRows(a, b, sort, axis);
    if (primary !== 0) return primary * sign;
    const byName = a.name.localeCompare(b.name, 'ru');
    if (byName !== 0) return byName;
    return a.venueId - b.venueId;
  });
}

// ---------------------------------------------------------------------------
// Derived values
// ---------------------------------------------------------------------------

/** Distinct regions of the CURRENT dataset, alphabetical — the picker's options. */
export function regionsOf(rows: ReadonlyArray<DashboardRowDto>): string[] {
  const regions = new Set<string>();
  for (const row of rows) {
    if (typeof row.region === 'string' && row.region.trim() !== '') regions.add(row.region);
  }
  return [...regions].sort((a, b) => a.localeCompare(b, 'ru'));
}

/**
 * Bar width for a fill percent: clamped to the track (a venue can be
 * over-subscribed — the number says so even when the bar is full) and rounded
 * to a tenth of a percent.
 */
export function barWidth(fillPercent: number | null | undefined): number {
  const value = typeof fillPercent === 'number' && Number.isFinite(fillPercent) ? fillPercent : 0;
  return Math.round(Math.min(Math.max(value, 0), 100) * 10) / 10;
}

// ---------------------------------------------------------------------------
// Drill-down
// ---------------------------------------------------------------------------

/** The venues page T.C3 reads these params (`venueId` presets the filter). */
export const APPLICATIONS_PATH = '/applications';

/**
 * `/applications?venueId=<id>` carrying the region along, so the drill-down lands
 * on the same slice of the campaign. A default state must not leak a parameter:
 * both optional values are dropped when unset.
 *
 * ADDITIVE (T.Z144, v1.9.37) — the `format` of the table's axis travels too: a click in
 * the «Онлайн» table drills into the venue's ONLINE applications, a click under the
 * «Офлайн» filter into its OFFLINE ones — the same parameter every widget card of the
 * page carries ({@link kindFormat}), so the register answers the rows the table showed.
 * The «вместе» offline table passes nothing: its drill keeps the pre-T.Z144 URL
 * (`?venueId=…` only), byte for byte.
 *
 * CORRECTED (T.Z156, v1.9.45) — the «вместе» byte-for-byte rule ends here: the axis
 * tables' «Заявления» cells now count their OWN format (v1.9.45), so an unfiltered
 * landing list would show MORE rows than the cell printed — the exact disagreement the
 * printed-count == register-count rule forbids. Every axis table therefore drills with
 * its own `format=` («вместе» included); only a genuinely axis-less row passes nothing.
 */
export function drillDownSearch(venueId: number, region?: string, format?: 'OFFLINE' | 'ONLINE'): string {
  const search = new URLSearchParams();
  search.set('venueId', String(venueId));
  if (typeof region === 'string' && region.trim() !== '') search.set('region', region);
  if (format === 'OFFLINE' || format === 'ONLINE') search.set('format', format);
  return `?${search.toString()}`;
}
